import React, { useEffect, useState } from 'react';
import { Input, Message, Popover } from '@meitu/saas-ui';
import type { Editor } from '@meitu/saas-editor-core';
import { trackConfig, type EventEmitter } from '@meitu/saas-editor-core/es/framework';
import { QuickStageOperate } from '@meitu/saas-editor-core/es/plugins/stage-operate';
import type { IExtraInfo, ILayerProps } from '@meitu/saas-editor-core/es/version-types';
import { getLocalIdentity } from '@meitu/xdesign-identity';
import { getEnterpriseID } from '@meitu/xdesign-widgets';
import GenBtn from '@meitu/xdesign-widgets/dist/meidouInfo/compoments/genBtn';
import { getTransferId } from '@meitu/xdesign-widgets/dist/meidouInfo/utils';
import { useLocaleStore } from '@/i18n';
import { useGeneratePPT } from '../../hooks/use-generate-ppt';
import { CloseBtn } from '../close-btn';
import { ModelTitle } from '../model-title';

const BTN_UUID = `create_single_page_submit`;

type RegenAiPPTProps = {
    editor: Editor;
    events: EventEmitter;
    isAiPPT: boolean;
    index: number;
    hoverIndex?: number;
    recordId: string;
    isAiRegen: boolean;
    // import { useInitPPT } from '@/hooks/useFetchTemplate/initPPT';
    initPPT: (stageIndex?: number) => void;
    regenIndex: number;
    setIsAiCreate: (isAiCreate: boolean) => void;
    setAiRegen: (data: { isAiRegen: boolean; regenIndex?: number }) => void;
    transferIdParams: any;
    isFromHuaweiCustom: () => boolean;
    getCurrentScene: () => string;
    // 从旧代码 const isFullscreen = useIsFullscreenState(); 搬迁
    isFullscreen: boolean;
    showCreateTip: (tip: string) => void;
    hideCreateTip: () => void;
    transferTemplateToClient: (templateDetail: any, fontList: any) => Promise<any>;
    packageTemplateConf: (
        templateConf: any,
        extraInfo: IExtraInfo,
        init: boolean,
    ) => {
        stages: ILayerProps[][];
        extraInfos: IExtraInfo[];
    };
    fetchPPTApplyItem: (params: any) => Promise<any>;
    fetchPPTCheckItemProcess: (params: any) => Promise<any>;
    fetchPPTItemDetail: (params: any) => Promise<any>;
    templateSubject: string;
};
// ai重新生成
export const RegenAiPPT: React.FC<RegenAiPPTProps> = React.memo(
    ({
        editor,
        events,
        isAiPPT,
        index,
        hoverIndex,
        recordId,
        setIsAiCreate,
        setAiRegen,
        isAiRegen,
        regenIndex,
        transferIdParams,
        isFromHuaweiCustom,
        getCurrentScene,
        isFullscreen,
        initPPT,
        showCreateTip,
        hideCreateTip,
        transferTemplateToClient,
        packageTemplateConf,
        fetchPPTApplyItem,
        fetchPPTCheckItemProcess,
        fetchPPTItemDetail,
        templateSubject,
    }) => {
        const { $t } = useLocaleStore();
        const showRegenModal = isAiRegen && regenIndex === index;
        const [needUpdate, setNeedUpdate] = useState(true);

        let extraInfo: IExtraInfo | null = null;
        if (showRegenModal) {
            extraInfo = editor.stage.getExtraInfo(index);
        }
        const [prompt, setPrompt] = useState('');

        const generatePPT = useGeneratePPT({
            editor,
            events,
            showCreateTip,
            hideCreateTip,
            transferTemplateToClient,
            packageTemplateConf,
            fetchPPTApplyItem,
            fetchPPTCheckItemProcess,
            fetchPPTItemDetail,
            templateSubject,
            prompt,
            setPrompt,
            tip: $t((v, { r }) => r(v.collapse.navigationCollapse.ppt.tips.pptReGenerating, [index + 1])),
            btnUuid: BTN_UUID,
            needUpdate: needUpdate && hoverIndex !== -1 && index === hoverIndex,
            setNeedUpdate,
            recordId,
            setIsAiCreate,
            setAiRegen,
        });

        const closePrompt = () => {
            setAiRegen({ isAiRegen: false });
        };
        // todo
        // useDownloadEvent(closePrompt);
        useEffect(() => {
            if (isFullscreen) {
                closePrompt();
            }
        }, [isFullscreen]);

        useEffect(() => {
            // 舞台快捷操作按钮点击事件
            const trackQuickOperateHandle = (type: string) => {
                closePrompt();
            };

            const quickClickHandler = editor.getInstance(QuickStageOperate).onQuickClick(trackQuickOperateHandle);
            return () => {
                editor.off(quickClickHandler);
            };
        }, []);

        useEffect(() => {
            if (showRegenModal) {
                setPrompt(extraInfo?.prompt || '');
            }
        }, [showRegenModal, extraInfo?.prompt]);

        const submitHandle = async () => {
            const transferId = await getTransferId({
                source: getCurrentScene(),
                oid: getLocalIdentity()?.oid || '',
                user_mode: getEnterpriseID() ? '1' : '',
                ...transferIdParams,
            });
            generatePPT.submit({
                click() {
                    window._stat_track('rebuild_submit');
                },
                fail() {
                    window._stat_track('rebuild_result', {
                        result: 0,
                    });
                },
                success(stages: ILayerProps[][], extraInfos: IExtraInfo[]) {
                    Message.success($t((v) => v.collapse.navigationCollapse.ppt.tips.generateSuccess));
                    console.log('templateDetail 33', stages, extraInfos);

                    editor.stage.replaceActive(stages[0], extraInfos[0], false, trackConfig.page.rebuild);
                    initPPT(index);
                    window._stat_track('rebuild_result', {
                        result: 1,
                    });
                },
                transferId,
            });
        };

        // 错误提示回调
        const handleErrorMessage = (msg: string) => {
            Message.error(msg);
        };

        if (!isAiPPT) return null;

        return (
            <>
                <Popover
                    placement="bottom"
                    overlayClassName="c-navigation-modal"
                    content={
                        <>
                            <CloseBtn
                                onClick={() => {
                                    setAiRegen({ isAiRegen: false });
                                    editor.enableOperate();
                                }}
                            />
                            <div className="c-ai-content">
                                <ModelTitle left={$t((v) => v.collapse.navigationCollapse.ppt.prompt)} />
                                <div className="c-ai-content__input">
                                    <Input.TextArea
                                        style={{ resize: 'none', fontSize: 14 }}
                                        className="input-text theme"
                                        maxLength={300}
                                        rows={8}
                                        value={prompt}
                                        autoFocus={true}
                                        onChange={({ target: { value } }) => {
                                            setPrompt(value.trim());
                                        }}
                                        onKeyDown={({ keyCode }: any) => {
                                            if (keyCode === 13) {
                                                submitHandle();
                                            }
                                        }}
                                        onFocus={(e: any) => {
                                            console.log('e?.target?.select', e?.target?.select);
                                            setTimeout(() => {
                                                e?.target?.select?.();
                                            }, 100);
                                        }}
                                        placeholder={extraInfo?.prompt}
                                        // onClick={() => {}}
                                    />
                                </div>

                                <GenBtn
                                    uuid={BTN_UUID}
                                    isFree={isFromHuaweiCustom()}
                                    className="c-navigation-collapse__btn-aippt"
                                    text={$t((v) => v.collapse.navigationCollapse.submit)}
                                    onGen={submitHandle}
                                    onErrorMessage={handleErrorMessage}
                                    source={getCurrentScene()}
                                />
                            </div>
                        </>
                    }
                    destroyTooltipOnHide
                    open={showRegenModal}
                >
                    <div className="c-navigation-modal-anchor"></div>
                </Popover>
            </>
        );
    },
);

RegenAiPPT.displayName = 'RegenAiPPT';
